import { Copy, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Avatar, AvatarStack } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { Card, MetricStrip } from '../../components/Card';
import { Checkbox, Radio, RadioGroup } from '../../components/Checkbox';
import { Chip, Dot } from '../../components/Chip';
import { Drawer } from '../../components/Drawer';
import { Field, FieldRow } from '../../components/Field';
import { Input, SearchInput, Textarea } from '../../components/Input';
import { Keys, MOD_KEY } from '../../components/Kbd';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { MultiSelect, Select } from '../../components/Select';
import { Skeleton, SkeletonLines } from '../../components/Skeleton';
import { PRIORITY_TONES, SLA_TONES, STAGE_TONES } from '../../components/status-tones';
import { ToneChip } from '../../components/Tone';
import {
  EmptyState,
  FilteredEmptyState,
  ForbiddenState,
  NotFoundState,
  ServerErrorState,
} from '../../components/system-states/SystemState';
import { formatDate, formatDateTime, formatNumber, formatRelative } from '../../lib/format';
import { useToasts } from '../../stores/toasts';
import { useUi } from '../../stores/ui';
import { DemoTable } from './DemoTable';
import styles from './DemoPage.module.css';

// The component demo page. This is the QA surface for S2 — the master plan's tests
// are run against this page on the deployed build, never against localhost.
//
// Each section names the spec it is demonstrating, so QA can check the rule rather
// than only the appearance.

const SELECT_OPTIONS = [
  { value: 'screening', label: 'Screening', description: 'CV review and sorting' },
  { value: 'assessment', label: 'Assessment', description: 'Written or practical test' },
  { value: 'interview', label: 'Interview', description: 'One to three rounds' },
  { value: 'offer', label: 'Offer', description: 'Approval and issue' },
];

/** Fixed at module load: calling Date.now() during render makes the value change
 *  on every re-render, which is exactly the instability React warns about. */
const TWO_DAYS_AGO = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);

const REJECTION_REASONS = [
  { value: 'skills', label: 'Skills mismatch', description: 'Does not meet a must-have criterion' },
  { value: 'experience', label: 'Not enough experience', description: 'Below the required years' },
  { value: 'salary', label: 'Salary expectation', description: 'Outside the approved band' },
];

export function DemoPage(): ReactNode {
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const openPalette = useUi((state) => state.openPalette);
  const theme = useUi((state) => state.theme);

  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [stage, setStage] = useState<string | null>('interview');
  const [tags, setTags] = useState<readonly string[]>(['screening', 'offer']);
  const [reason, setReason] = useState('skills');
  const [isChecked, setIsChecked] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const now = new Date();

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <span className={styles.eyebrow}>S2 · design system</span>
          <h1>Component demo</h1>
        </div>
        <div className={styles.row}>
          <Button variant="quiet" onClick={openPalette}>
            Open palette
          </Button>
          <Chip tone="progress" emphasis="solid">
            {theme} mode
          </Chip>
        </div>
      </header>

      <p className={styles.note}>
        Every component on this page is built on the Flock design tokens and works in both
        light and dark mode. Use the sun or moon button in the top bar to switch, and check
        that nothing becomes hard to read. Press <Keys keys={[MOD_KEY, 'K']} /> anywhere for
        the command palette, or <Keys keys={['?']} /> for the shortcut sheet.
      </p>

      <Section title="Colour tokens" reference="Design System/tokens/colors.css">
        <div className={styles.swatches}>
          {[
            ['--nr-success', '--nr-success-ink', 'success'],
            ['--nr-warning', '--nr-warning-ink', 'warning'],
            ['--nr-danger', '--nr-danger-ink', 'danger'],
            ['--nr-progress', '--nr-progress-ink', 'in progress'],
            ['--nr-neutral', '--nr-neutral-ink', 'neutral'],
            ['--nr-attention', '--nr-attention-ink', 'attention'],
          ].map(([fill, ink, name]) => (
            <div key={fill} className={styles.swatch}>
              {/* The token being demonstrated is DATA here, not a styling choice —
                  so it is passed as a CSS custom property and the stylesheet still
                  owns every appearance rule. */}
              <span className={styles.swatchFill} style={swatchVars(fill, ink)}>
                {name}
              </span>
              <span className={styles.swatchName}>{fill}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Buttons" reference="Design System · pill actions">
        <div className={styles.row}>
          <Button variant="solid">Primary action</Button>
          <Button variant="quiet">Quiet</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Reject</Button>
          <Button variant="solid" icon={Plus}>
            With icon
          </Button>
          <Button variant="solid" isPending>
            Saving
          </Button>
          <Button variant="quiet" disabled>
            Disabled
          </Button>
          <Button variant="quiet" size="sm">
            Small
          </Button>
          <Button variant="quiet" size="lg">
            Large
          </Button>
          <Button variant="quiet" icon={Copy} aria-label="Copy" />
        </div>
      </Section>

      <Section title="Status chips and dots" reference="06-qol §11">
        <div className={styles.row}>
          <ToneChip tone={STAGE_TONES.offer}>Offer</ToneChip>
          <ToneChip tone={SLA_TONES.warning}>SLA at risk</ToneChip>
          <ToneChip tone={STAGE_TONES.rejected}>Rejected</ToneChip>
          <ToneChip tone={STAGE_TONES.interview_1}>Interview</ToneChip>
          <ToneChip tone={STAGE_TONES.hold}>On hold</ToneChip>
          <Chip>Tonal</Chip>
          <Chip plain>Bangla · English</Chip>
          <Chip plain onRemove={() => undefined}>
            Removable filter
          </Chip>
          <Dot tone={PRIORITY_TONES.urgent}>High priority</Dot>
          <Dot tone={PRIORITY_TONES.low}>Low priority</Dot>
        </div>
      </Section>

      <Section title="Avatars" reference="Design System · no photography">
        <div className={styles.row}>
          <Avatar name="Rafiq Hossain" size="sm" />
          <Avatar name="Nadia Islam" />
          <Avatar name="Tanvir Hussain" size="lg" />
          <Avatar name="System" isSystem />
          <AvatarStack names={['Rafiq Hossain', 'Nadia Islam', 'Imran Kabir', 'Mitu Barua']} />
        </div>
      </Section>

      <Section title="Form controls" reference="CLAUDE.md §5 · never a stock browser control">
        <div className={styles.grid}>
          <Card>
            <FieldRow>
              <Field label="Candidate name" isRequired hint="As it appears on their CV">
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onClear={() => setText('')}
                    placeholder="Rafiq Hossain"
                  />
                )}
              </Field>
              <Field label="Expected salary" hint="Monthly, gross">
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    prefix="৳"
                    isNumeric
                    placeholder="0"
                  />
                )}
              </Field>
            </FieldRow>

            <Field label="Email" error="Enter an email address, for example name@example.com">
              {({ inputId, describedBy, isInvalid }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  isInvalid={isInvalid}
                  defaultValue="not-an-email"
                />
              )}
            </Field>

            <Field label="Search">
              {({ inputId }) => <SearchInput id={inputId} placeholder="Search candidates" />}
            </Field>
          </Card>

          <Card>
            <Field label="Stage" isRequired>
              {({ inputId, describedBy }) => (
                <Select
                  id={inputId}
                  describedBy={describedBy}
                  label="Stage"
                  options={SELECT_OPTIONS}
                  value={stage}
                  onChange={setStage}
                />
              )}
            </Field>

            <Field label="Tags" hint="Choose as many as apply">
              {({ inputId, describedBy }) => (
                <MultiSelect
                  id={inputId}
                  describedBy={describedBy}
                  label="Tags"
                  options={SELECT_OPTIONS}
                  value={tags}
                  onChange={setTags}
                />
              )}
            </Field>

            <Field label="Notes" hint="Visible to the hiring team only">
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  maxLength={200}
                  placeholder="Anything the panel should know"
                />
              )}
            </Field>
          </Card>

          <Card title="Rejection reason" meta="Every rejection requires a reason — non-negotiable #2">
            <RadioGroup>
              {REJECTION_REASONS.map((option) => (
                <Radio
                  key={option.value}
                  name="reason"
                  value={option.value}
                  checked={reason === option.value}
                  onChange={setReason}
                  label={option.label}
                  description={option.description}
                />
              ))}
            </RadioGroup>
            <div className={styles.row}>
              <Checkbox checked={isChecked} onChange={setIsChecked} label="Notify the candidate" />
              <Checkbox checked={false} onChange={() => undefined} label="Add to talent pool" />
              <Checkbox checked isIndeterminate onChange={() => undefined} label="Partial" />
            </div>
          </Card>
        </div>
      </Section>

      <Section title="Overlays" reference="CLAUDE.md §5 · anchored, not centred">
        <div className={styles.row}>
          <Button variant="quiet" onClick={() => setIsModalOpen(true)}>
            Open modal
          </Button>
          <Button variant="danger" onClick={() => setIsConfirmOpen(true)}>
            Destructive confirm
          </Button>
          <Button variant="quiet" onClick={() => setIsDrawerOpen(true)}>
            Open drawer
          </Button>
          <Button
            variant="quiet"
            onClick={() => showToast({ tone: 'success', message: 'Candidate advanced' })}
          >
            Toast
          </Button>
          <Button
            variant="quiet"
            onClick={() =>
              showToast({
                tone: 'danger',
                message: 'Could not save the scorecard',
                detail: 'The interview was closed by someone else. Reopen it and try again.',
              })
            }
          >
            Error toast
          </Button>
          <Button
            variant="quiet"
            onClick={() => showUndo('Candidate rejected', () => showToast({ tone: 'info', message: 'Rejection undone' }))}
          >
            Undo toast
          </Button>
        </div>
      </Section>

      <Section title="Metrics" reference="Design System · one segmented strip">
        <Card isFlush>
          <MetricStrip
            metrics={[
              { label: 'Open jobs', value: '12', onClick: () => undefined },
              { label: 'In pipeline', value: '348', onClick: () => undefined },
              { label: 'Interviews this week', value: '19' },
              { label: 'Offers out', value: '4' },
              { label: 'Median days to hire', value: '31' },
            ]}
          />
        </Card>
      </Section>

      <Section title="Dates, times and empty values" reference="06-qol §11">
        <Card>
          <dl className={styles.row}>
            <Fact label="Date">{formatDate(now)}</Fact>
            <Fact label="Date and time">{formatDateTime(now)}</Fact>
            <Fact label="Relative">{formatRelative(TWO_DAYS_AGO)}</Fact>
            <Fact label="Number">{formatNumber(1234567)}</Fact>
            <Fact label="Empty">{null}</Fact>
          </dl>
          <p className={styles.note}>
            Times always carry a timezone abbreviation, dates are DD-MM-YYYY, and an empty
            value is always an em dash — never blank, never &ldquo;N/A&rdquo;.
          </p>
        </Card>
      </Section>

      <Section title="Loading skeletons" reference="SYS-02">
        <div className={styles.grid}>
          <Card title="Never a full-page spinner">
            <Skeleton shape="title" />
            <SkeletonLines count={4} />
          </Card>
          <Card title="Shaped like the content">
            <div className={styles.row}>
              <Skeleton shape="circle" />
              <Skeleton width="40%" />
            </div>
            <Skeleton shape="card" />
          </Card>
        </div>
      </Section>

      <Section title="System states" reference="SYS-01 to SYS-06">
        <div className={styles.grid}>
          <div className={styles.stateFrame}>
            <EmptyState
              isInset
              title="No candidates yet"
              body="Share the posting link and applications will appear here as they arrive."
              action={<Button variant="solid">Copy posting link</Button>}
            />
          </div>
          <div className={styles.stateFrame}>
            <FilteredEmptyState isInset filterCount={2} onClearFilters={() => undefined} />
          </div>
          <div className={styles.stateFrame}>
            <ForbiddenState
              isInset
              resource="offer amounts"
              contact="Tanvir Hussain"
            />
          </div>
          <div className={styles.stateFrame}>
            <NotFoundState onGoBack={() => undefined} />
          </div>
          <div className={styles.stateFrame}>
            <ServerErrorState requestId="req_8f2c41" onRetry={() => undefined} />
          </div>
        </div>
        <p className={styles.note}>
          The sixth state is the offline banner (SYS-06). It is wired to the realtime socket
          rather than only to the browser&rsquo;s network flag, so it also appears when the
          connection to live updates drops while the network itself is fine. Turn off wifi to
          see it.
        </p>
      </Section>

      <Section title="Data table" reference="06-qol §3 · 5,000 rows">
        <p className={styles.note}>
          Click a column header to sort, shift-click a second header to sort by both. Drag a
          column edge to resize it. Click row 3, then shift-click row 9, to select the range.
          Select every row on the page and an option appears to select all matching rows
          instead. Filters and sorting are written into the address bar, so copying the URL
          shares the exact view.
        </p>
        <div className={styles.tableFrame}>
          <DemoTable />
        </div>
      </Section>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Schedule an interview"
        description="This is an ordinary dialog. Esc closes it and focus returns to the button that opened it."
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="solid" onClick={() => setIsModalOpen(false)}>
              Schedule
            </Button>
          </>
        }
      >
        <Field label="Round" isRequired>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Round"
              options={SELECT_OPTIONS}
              value={stage}
              onChange={setStage}
            />
          )}
        </Field>
      </Modal>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => setIsConfirmOpen(false)}
        title="Cancel this job"
        description="The job will close and every candidate still in its pipeline will be moved to the talent pool."
        notice="348 candidates are currently in this pipeline. This cannot be undone."
        confirmLabel="Cancel job"
      />

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Rafiq Hossain"
        meta="Senior accountant · Nest Operations"
        footer={
          <Button variant="solid" onClick={() => setIsDrawerOpen(false)}>
            Done
          </Button>
        }
      >
        <SkeletonLines count={6} />
      </Drawer>
    </div>
  );
}

function Section({
  title,
  reference,
  children,
}: {
  title: string;
  reference: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <span className={styles.sectionRef}>{reference}</span>
      </div>
      {children}
    </section>
  );
}

/** Custom properties are not part of React's CSSProperties, so they travel as a
 *  plain string record — which `style` accepts without a type assertion. */
function swatchVars(fill: string, ink: string): Record<string, string> {
  return { '--swatch-fill': `var(${fill})`, '--swatch-ink': `var(${ink})` };
}

function Fact({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className={styles.swatchName}>{label}</dt>
      <dd>{children ?? '—'}</dd>
    </div>
  );
}
